'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/components/auth-provider';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import {
  AlertTriangle,
  Ban,
  Check,
  ExternalLink,
  Flag,
  Gavel,
  RefreshCw,
  ShieldAlert,
  UserX,
  X,
} from 'lucide-react';

/**
 * Moderation desk (DSA notice-and-action, workstream A). The queue, the case
 * view with the prohibited-services checklist from the moderation runbook,
 * the review form, and the statement-of-reasons preview.
 *
 * Staff work the queue; the account-suspension resolution is admin only,
 * enforced by the API (a hidden control is not a permission check).
 */

interface QueueRow {
  id: string;
  targetType: string;
  targetId: string;
  flagType: string;
  status: string;
  priority: number;
  reason: string | null;
  createdAt: string;
  reviewedAt: string | null;
  resolution: string | null;
  reporter: { id: string; username: string | null; accountType: string };
  targetPreview: string | null;
}

interface QueueData {
  rows: QueueRow[];
  counts: { byStatus: Record<string, number>; byPriority: Record<string, number> };
}

interface CaseData {
  flag: QueueRow & { evidence: unknown; provision: string | null; reviewNotes: string | null; sorSentAt: string | null };
  reporter: { id: string; username: string | null; accountType: string; reputationScore: string | null; createdAt: string } | null;
  target: {
    exists: boolean;
    label?: string;
    excerpt?: string | null;
    principalId?: string | null;
    suspendedAt?: string | null;
    url?: string | null;
  };
  priorFlags: Array<{ id: string; flagType: string; status: string; reason: string | null; createdAt: string }>;
}

const PRIORITY_LABELS = ['P0', 'P1', 'P2', 'P3'];

/** Runbook section 3, rendered as the per-case review checklist. */
const REVIEW_CHECKLIST = [
  'Unlicensed professional services: legal, medical, financial, investment, insurance, accounting, or tax advice without a demonstrable license. Generic information with a clear posture is acceptable; individualized advice is not.',
  'Impersonation or AI transparency: agent presented as a human, or identity or authority misrepresented.',
  'Randomized paid items (KR): purchasable randomized rewards without clearly disclosed probability rates. Unlist pending fix and escalate to legal@.',
  'Rewards or attribution manipulation: manufactured usage, reciprocal engagement rings, attribution stripping.',
  'Illegal or infringing content: CSAE (immediate removal, preservation, report), IP infringement, violence, harassment, hate.',
  'Designated prohibited category: check the current list owned by legal@.',
];

const RESOLUTION_OPTIONS: Array<{ value: string; label: string; adminOnly?: boolean; forTypes?: string[] }> = [
  { value: 'none', label: 'No action (record only)' },
  { value: 'content_removed', label: 'Remove the content', forTypes: ['post', 'comment'] },
  { value: 'listing_unlisted', label: 'Unlist the app from browsing', forTypes: ['widget', 'agent'] },
  { value: 'account_suspended', label: 'Suspend the account', adminOnly: true },
];

export function ModerationTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [data, setData] = useState<QueueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [openCase, setOpenCase] = useState<CaseData | null>(null);
  const [caseLoading, setCaseLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const r = await fetch(`/api/admin/moderation/queue${qs}`, { credentials: 'same-origin' });
      if (r.status === 403) {
        setForbidden(true);
        return;
      }
      if (!r.ok) throw new Error(`Request failed (${r.status})`);
      setData((await r.json()) as QueueData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const openFlag = useCallback(async (id: string) => {
    setCaseLoading(true);
    try {
      const r = await fetch(`/api/admin/moderation/flags/${id}`, { credentials: 'same-origin' });
      if (!r.ok) throw new Error(`Request failed (${r.status})`);
      setOpenCase((await r.json()) as CaseData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the case');
    } finally {
      setCaseLoading(false);
    }
  }, []);

  if (forbidden) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
        <ShieldAlert className="h-4 w-4" />
        This area is for moderators and admins.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Counts strip */}
      <div className="flex items-center gap-2 flex-wrap">
        {['active', 'upheld', 'rejected', 'expired'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={cn(
              'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
              statusFilter === s
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border text-muted-foreground hover:text-foreground',
            )}
          >
            {s} {data?.counts.byStatus[s] ?? 0}
          </button>
        ))}
        <div className="flex-1" />
        {Object.entries(data?.counts.byPriority ?? {}).map(([p, n]) => (
          <span
            key={p}
            className={cn(
              'px-2 py-0.5 rounded-full text-xs',
              p === '0' ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground',
            )}
          >
            {PRIORITY_LABELS[Number(p)] ?? `P${p}`}: {n}
          </span>
        ))}
        <button
          onClick={() => void load()}
          className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground"
          title="Refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4" /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner size="lg" tone="muted" />
        </div>
      ) : openCase ? (
        <CaseView
          data={openCase}
          isAdmin={isAdmin}
          onClose={() => setOpenCase(null)}
          onDone={() => {
            setOpenCase(null);
            void load();
          }}
        />
      ) : (
        <div className="border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="py-2 px-3 font-medium">Priority</th>
                <th className="py-2 px-3 font-medium">Target</th>
                <th className="py-2 px-3 font-medium">Type</th>
                <th className="py-2 px-3 font-medium">Reporter</th>
                <th className="py-2 px-3 font-medium">Reported</th>
                <th className="py-2 px-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((row) => (
                <tr key={row.id} className="border-b border-border/60 hover:bg-accent/40">
                  <td className="py-2 px-3">
                    <span
                      className={cn(
                        'px-1.5 py-0.5 rounded text-xs font-medium',
                        row.priority === 0
                          ? 'bg-destructive/10 text-destructive'
                          : row.priority === 1
                            ? 'bg-amber-500/10 text-amber-600'
                            : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {PRIORITY_LABELS[row.priority] ?? `P${row.priority}`}
                    </span>
                  </td>
                  <td className="py-2 px-3 max-w-[220px] truncate">
                    {row.targetPreview ?? row.targetId.slice(0, 8)}
                    <span className="text-muted-foreground text-xs ml-1.5">{row.targetType}</span>
                  </td>
                  <td className="py-2 px-3">
                    <span className="inline-flex items-center gap-1 text-xs">
                      <Flag className="h-3 w-3" /> {row.flagType}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-xs text-muted-foreground">
                    {row.reporter.username ? `@${row.reporter.username}` : row.reporter.accountType}
                  </td>
                  <td className="py-2 px-3 text-xs text-muted-foreground">
                    {new Date(row.createdAt).toLocaleDateString()}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <button
                      onClick={() => void openFlag(row.id)}
                      className="text-xs px-2.5 py-1 rounded-lg border border-border hover:bg-accent"
                      disabled={caseLoading}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
              {(data?.rows ?? []).length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                    No {statusFilter} reports. That is a good day.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CaseView({
  data,
  isAdmin,
  onClose,
  onDone,
}: {
  data: CaseData;
  isAdmin: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { flag, reporter, target, priorFlags } = data;
  const [decision, setDecision] = useState<'uphold' | 'reject'>('uphold');
  const [resolution, setResolution] = useState<string>('none');
  const [provision, setProvision] = useState('');
  const [notes, setNotes] = useState('');
  const [channelId, setChannelId] = useState('');
  const [automated, setAutomated] = useState(false);
  const [escalate, setEscalate] = useState(false);
  const [escalateAuthority, setEscalateAuthority] = useState('');
  const [escalateNotes, setEscalateNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checks, setChecks] = useState<boolean[]>(() => REVIEW_CHECKLIST.map(() => false));

  const options = useMemo(
    () =>
      RESOLUTION_OPTIONS.filter(
        (o) =>
          (!o.adminOnly || isAdmin) && (!o.forTypes || o.forTypes.includes(flag.targetType)),
      ),
    [isAdmin, flag.targetType],
  );

  const effectiveResolution = decision === 'reject' ? 'none' : resolution;

  const submit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch(`/api/admin/moderation/flags/${flag.id}/review`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          decision,
          resolution: effectiveResolution,
          provision: provision.trim() || undefined,
          reviewNotes: notes.trim() || undefined,
          channelId: channelId.trim() || undefined,
          automatedMeansUsed: automated,
          ...(escalate && escalateAuthority.trim()
            ? {
                art18Escalation: {
                  authority: escalateAuthority.trim(),
                  notes: escalateNotes.trim() || undefined,
                },
              }
            : {}),
        }),
      });
      const body = (await r.json()) as { error?: string; warning?: string };
      if (!r.ok) throw new Error(body.error ?? `Request failed (${r.status})`);
      if (body.warning) {
        setError(body.warning);
        return;
      }
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Review failed');
    } finally {
      setSubmitting(false);
    }
  }, [flag.id, decision, effectiveResolution, provision, notes, channelId, automated, escalate, escalateAuthority, escalateNotes, onDone]);

  const alreadyReviewed = !!flag.reviewedAt;

  return (
    <div className="space-y-4">
      <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground">
        &larr; Back to the queue
      </button>

      {/* Target */}
      <div className="border border-border rounded-xl p-4 space-y-2">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{target.label ?? flag.targetPreview ?? flag.targetId}</span>
          <span className="text-xs text-muted-foreground">{flag.targetType}</span>
          {target.url && (
            <a
              href={target.url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              open <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {!target.exists && (
            <span className="text-xs text-destructive">target no longer exists</span>
          )}
          {target.suspendedAt && (
            <span className="text-xs text-amber-600 inline-flex items-center gap-1">
              <UserX className="h-3 w-3" /> account suspended
            </span>
          )}
        </div>
        {target.excerpt && (
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{target.excerpt}</p>
        )}
        <div className="text-xs text-muted-foreground">
          Reported as <span className="font-medium">{flag.flagType}</span>
          {flag.reason ? <> : &ldquo;{flag.reason}&rdquo;</> : null}
          {' '}by {reporter?.username ? `@${reporter.username}` : reporter?.accountType ?? 'unknown'}
          {priorFlags.length > 0 && <> · {priorFlags.length} other report{priorFlags.length === 1 ? '' : 's'} on this target</>}
        </div>
      </div>

      {/* Checklist */}
      <div className="border border-border rounded-xl p-4">
        <div className="text-sm font-medium mb-2">Review checklist</div>
        <div className="space-y-2">
          {REVIEW_CHECKLIST.map((item, i) => (
            <label key={i} className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={checks[i]}
                onChange={(e) =>
                  setChecks((c) => c.map((v, j) => (j === i ? e.target.checked : v)))
                }
                className="mt-0.5"
              />
              <span>{item}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Verdict */}
      {alreadyReviewed ? (
        <div className="border border-border rounded-xl p-4 text-sm text-muted-foreground">
          Reviewed {flag.reviewedAt ? new Date(flag.reviewedAt).toLocaleString() : ''} ·{' '}
          {flag.status} · {flag.resolution ?? 'no action'}
          {flag.sorSentAt && <> · statement of reasons sent</>}
        </div>
      ) : (
        <div className="border border-border rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Gavel className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Decision</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setDecision('uphold')}
              className={cn(
                'px-3 py-1.5 rounded-lg text-sm border inline-flex items-center gap-1.5',
                decision === 'uphold'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground',
              )}
            >
              <Check className="h-3.5 w-3.5" /> Uphold
            </button>
            <button
              onClick={() => setDecision('reject')}
              className={cn(
                'px-3 py-1.5 rounded-lg text-sm border inline-flex items-center gap-1.5',
                decision === 'reject'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground',
              )}
            >
              <X className="h-3.5 w-3.5" /> Reject
            </button>
          </div>

          {decision === 'uphold' && (
            <>
              <label className="block text-xs text-muted-foreground">
                Action
                <select
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                >
                  {options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              {resolution === 'listing_unlisted' && (
                <label className="block text-xs text-muted-foreground">
                  Listing channel id
                  <input
                    value={channelId}
                    onChange={(e) => setChannelId(e.target.value)}
                    placeholder="the app's listing channel uuid"
                    className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  />
                </label>
              )}
              <label className="block text-xs text-muted-foreground">
                Provision violated (cited in the statement of reasons)
                <input
                  value={provision}
                  onChange={(e) => setProvision(e.target.value)}
                  placeholder="Terms of Service 6, prohibited services"
                  className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </label>
            </>
          )}

          <label className="block text-xs text-muted-foreground">
            Facts and circumstances (goes into the notice)
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="What was found, where, when, and how it came to our attention."
              className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>

          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <input type="checkbox" checked={automated} onChange={(e) => setAutomated(e.target.checked)} />
            Automated means contributed to this decision (disclosed in the notice)
          </label>

          {/* DSA Art. 18: suspicion of a criminal offence threatening life or
              safety must be reported to the authorities of the member state
              concerned. The checkbox records that the reviewer escalated;
              the record is stored with the flag. Procedure and owner:
              docs/internal/launch/moderation-runbook.md. */}
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={escalate}
              onChange={(e) => setEscalate(e.target.checked)}
            />
            Escalated to law enforcement (suspected offence threatening life or safety)
          </label>
          {escalate && (
            <div className="space-y-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
              <label className="block text-xs">
                <span className="text-muted-foreground">Authority informed (or being informed)</span>
                <input
                  value={escalateAuthority}
                  onChange={(e) => setEscalateAuthority(e.target.value)}
                  placeholder="e.g. Bundeskriminalamt online report, case/reference if known"
                  className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-xs">
                <span className="text-muted-foreground">Escalation notes</span>
                <textarea
                  value={escalateNotes}
                  onChange={(e) => setEscalateNotes(e.target.value)}
                  rows={2}
                  placeholder="What raised the suspicion, what was sent, who owns follow-up."
                  className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </label>
            </div>
          )}

          {/* SoR preview */}
          {decision === 'uphold' && (
            <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground space-y-1">
              <div className="font-medium text-foreground">Statement of reasons preview</div>
              <div>Decision: {RESOLUTION_OPTIONS.find((o) => o.value === resolution)?.label}</div>
              <div>Provision: {provision.trim() || '(required)'}</div>
              <div>Facts: {notes.trim() || '(from your notes above)'}</div>
              <div>Redress: contest at legal@robutler.ai (ToS 22, 26.3)</div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" /> {error}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={() => void submit()}
              disabled={submitting || (decision === 'uphold' && !provision.trim())}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {submitting ? <Spinner size="sm" /> : <Ban className="h-3.5 w-3.5" />}
              {decision === 'uphold' ? 'Uphold and act' : 'Reject the report'}
            </button>
            <span className="text-xs text-muted-foreground">
              The affected person gets a notice; the reporter gets the outcome.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
